import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import type { Bundesland } from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { ZipCity } from '../../adresse.js';
import { KpfFilter } from '../../filter/KpfFilter.js';
import { StudentFilter } from '../../filter/StudentFilter.js';
import { BundeslandAspect } from '../../master-data-management/aspects/BundeslandAspect.js';
import { ZipCityAspect } from '../../master-data-management/aspects/ZipCityAspect.js';
import { KPFAPI } from '../../master-data-management/kpf/api/KPFAPI.js';
import { KPFMultipleAspect } from '../../master-data-management/kpf/entities/aspects/KPFMultipleAspect.js';
import { Partner } from '../../master-data-management/partner/entities/Partner.js';
import { UserAPI } from '../../master-data-management/users/api/UserAPI.js';
import { Mitarbeiter } from '../../master-data-management/users/entities/Mitarbeiter.js';
import { ReportingApplication } from './ReportingApplication.js';

export enum ReportingGoalState {
  GREEN = 'GREEN',
  YELLOW = 'YELLOW',
  RED = 'RED',
  UNKNOWN = 'UNKNOWN',
}

export interface ReportingGoalCurrentState {
  state: ReportingGoalState;
  description: string;
  percentage?: number;
}

export function reportingGoalStateColor(state: ReportingGoalState): string {
  switch (state) {
    case ReportingGoalState.GREEN:
      return 'green';
    case ReportingGoalState.YELLOW:
      return 'orange';
    case ReportingGoalState.RED:
      return 'red';
    default:
      return 'gray';
  }
}

@Entity()
export class ReportingGoal extends MongoEntity {
  static override _class = 'ReportingGoal';
  static override _collectionName = 'reporting-goals';
  static _name = '';

  @Field(() => String)
  applicationID: Maybe<string>;

  @DataLoadedField(() => ReportingApplication, ({ applicationID }) => applicationID)
  application: Maybe<ReportingApplication>;

  @Field(() => String)
  title: Maybe<string>;

  @Field(() => String)
  summary: Maybe<string>;

  @DataLoadedField(() => Mitarbeiter, ({ responsibleIDs }) => responsibleIDs)
  responsible: Maybe<Mitarbeiter>;

  @Field(() => [String])
  responsibleIDs: Maybe<string[]>;

  @Field(() => Int)
  priority: Maybe<number>;

  @Field(() => String)
  priorityDescription: Maybe<string>;

  async currentState(day: DateTime = DateTime.now()): Promise<ReportingGoalCurrentState> {
    return { state: ReportingGoalState.UNKNOWN, description: '' };
  }

  useForAverageCalculation() {
    return false;
  }
}

@Entity()
export class ReportingGoalManual extends ReportingGoal {
  static override _class = 'ReportingGoalManual';
  static override _name = 'Weiches Ziel: manuell';

  @Field(() => String)
  state: Maybe<ReportingGoalState>;

  @Field(() => String)
  stateDescription: Maybe<string>;

  override async currentState(day: DateTime = DateTime.now()): Promise<ReportingGoalCurrentState> {
    return { state: this.state ?? ReportingGoalState.UNKNOWN, description: this.stateDescription ?? '' };
  }
}

@Entity()
export class ReportingGoalManualHard extends ReportingGoalManual {
  static override _class = 'ReportingGoalManualHard';
  static override _name = 'Hartes Ziel: manuell';

  override useForAverageCalculation(): boolean {
    return true;
  }
}

@Entity()
export class ReportingGoalAbsActiveStudents extends ReportingGoal {
  static override _class = 'ReportingGoalAbsActiveStudents';
  static override _name = 'Hartes Ziel: aktive Schüler:innen';

  @Field(() => Float)
  target: Maybe<number>;

  @Field(() => ZipCity)
  city: Maybe<ZipCity>;

  @Field(() => String)
  state: Maybe<Bundesland>;

  @Field(() => [String])
  partners: Maybe<string[]>;

  @Field(() => [String])
  products: Maybe<string[]>;

  override async currentState(day: DateTime = DateTime.now()): Promise<ReportingGoalCurrentState> {
    if (!this.target || this.target === 0) {
      return { state: ReportingGoalState.UNKNOWN, description: 'Es wurde kein Zielwert definiert.' };
    }

    const filter = await this._buildKPFFilter();
    if (filter.aspects.length === 0) {
      return { state: ReportingGoalState.UNKNOWN, description: 'Dieses Ziel fehlen die notwendigen Filterkriterien.' };
    }

    const kpfs = await firstValueFrom(KPFAPI.subscribeByFilter(0, 1000, filter)({ _id: 1 }));

    const count = await UserAPI.getCountByFilter(
      new StudentFilter({
        activeOnDay: day.toJSDate(),
        aspects: [
          new BooleanAspect({ fieldName: 'active', value: true }),
          ...((kpfs?.length ?? 0) > 0
            ? [new KPFMultipleAspect({ customID: 'kpfsMultiple', values: (kpfs ?? []).map(kpf => kpf._id) })]
            : []),
        ],
      }),
    )();

    const percentage = (count / (this.target ?? 1)) * 100;

    // 0 - 50   : RED
    // 50 - 80  : YELLOW
    // 80+      : GREEN
    if (percentage < 50) {
      return {
        state: ReportingGoalState.RED,
        description: `Es sind nur ${count} von ${this.target} aktiven Schüler:innen vorhanden.`,
        percentage,
      };
    } else if (percentage < 80) {
      return {
        state: ReportingGoalState.YELLOW,
        description: `Es sind ${count} von ${this.target} aktiven Schüler:innen vorhanden.`,
        percentage,
      };
    }

    return {
      state: ReportingGoalState.GREEN,
      description: `Es sind ${count} von ${this.target} aktiven Schüler:innen vorhanden.`,
      percentage,
    };
  }

  private async _buildKPFFilter() {
    const filter = new KpfFilter({});

    if (this.state) {
      filter.aspects.push(new BundeslandAspect({ customID: 'stateOfPartner', value: this.state }));
    }

    if (this.city) {
      filter.aspects.push(new ZipCityAspect({ customID: 'zipCityOfPartner', zipCity: this.city }));
    }

    if (this.partners && this.partners.length > 0) {
      const partners = await Partner.getByID<Partner>(this.partners ?? [])({ name: 1 });

      filter.aspects.push(
        new StringAspect({ customID: 'partnerName', value: partners.map(p => p.name).join(','), mode: 'containsOR' }),
      );
    }

    return filter;
  }

  override useForAverageCalculation(): boolean {
    return true;
  }
}

export const ReportingGoalClasses = {
  [ReportingGoalManual._class]: ReportingGoalManual,
  [ReportingGoalManualHard._class]: ReportingGoalManualHard,
  [ReportingGoalAbsActiveStudents._class]: ReportingGoalAbsActiveStudents,
};
